import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useHarvestComponents, useCompanyDisabledHarvestComponentIds } from "@/hooks/useHarvestComponents";

type Props =
  | { mode: "company"; companyId: string | null; categories: string[] }
  | { mode: "default"; companyId?: undefined; categories: string[] };

/**
 * Brand × appliance-type harvest selection.
 * - default mode: edits platform defaults (brand_harvest_components, superadmin only).
 * - company mode: edits this company's include/exclude overrides on top of the defaults.
 */
export default function BrandHarvestSelector(props: Props) {
  const { mode, categories } = props;
  const companyId = props.mode === "company" ? props.companyId : null;
  const { toast } = useToast();
  const qc = useQueryClient();
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [confirmReset, setConfirmReset] = useState(false);

  const { data: components = [] } = useHarvestComponents();
  const { data: disabledIds = new Set<string>() } = useCompanyDisabledHarvestComponentIds();

  const { data: brands = [] } = useQuery({
    queryKey: ["brands"],
    queryFn: async () => {
      const { data, error } = await supabase.from("brands").select("name").order("name");
      if (error) throw error;
      return data as { name: string }[];
    },
  });

  const ready = !!brand && !!category;

  const { data: defaults = [] } = useQuery({
    queryKey: ["brand_harvest_components", brand, category],
    enabled: ready,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brand_harvest_components")
        .select("id, component_id, appliance_type")
        .ilike("brand", brand)
        .or(`appliance_type.is.null,appliance_type.eq.${category}`);
      if (error) throw error;
      return data;
    },
  });

  const { data: overrides = [] } = useQuery({
    queryKey: ["company_brand_harvest_overrides", companyId, brand, category],
    enabled: ready && mode === "company" && !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("company_brand_harvest_overrides")
        .select("id, component_id, mode")
        .eq("company_id", companyId!)
        .ilike("brand", brand)
        .eq("appliance_type", category);
      if (error) throw error;
      return data;
    },
  });

  const defaultIds = new Set(defaults.map((d) => d.component_id));
  const ovById = new Map(overrides.map((o) => [o.component_id, o]));

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["brand_harvest_components"] });
    qc.invalidateQueries({ queryKey: ["company_brand_harvest_overrides"] });
    qc.invalidateQueries({ queryKey: ["harvest_components_for_brand"] });
    qc.invalidateQueries({ queryKey: ["harvest_recommendations"] });
  };
  const assertChanged = (data: unknown[] | null) => {
    if (!data || data.length === 0) throw new Error("Nothing was saved — you may not have permission.");
  };

  const setSelected = useMutation({
    mutationFn: async ({ componentId, on }: { componentId: string; on: boolean }) => {
      if (mode === "default") {
        if (on) {
          const { data, error } = await supabase.from("brand_harvest_components")
            .insert({ brand, component_id: componentId, appliance_type: category }).select("id");
          if (error) throw error; assertChanged(data);
        } else {
          const { data, error } = await supabase.from("brand_harvest_components").delete()
            .ilike("brand", brand).eq("component_id", componentId).eq("appliance_type", category).select("id");
          if (error) throw error; assertChanged(data);
        }
        return;
      }
      if (!companyId) throw new Error("No active company");
      const existing = ovById.get(componentId);
      const isDefault = defaultIds.has(componentId);
      // Desired: on. Default → remove exclude. Non-default → add include.
      // Desired: off. Default → add exclude. Non-default → remove include.
      if (existing && ((on && existing.mode === "exclude") || (!on && existing.mode === "include"))) {
        const { data, error } = await supabase.from("company_brand_harvest_overrides").delete().eq("id", existing.id).select("id");
        if (error) throw error; assertChanged(data);
        return;
      }
      if ((on && !isDefault) || (!on && isDefault)) {
        const { data, error } = await supabase.from("company_brand_harvest_overrides").insert({
          company_id: companyId, brand, appliance_type: category, component_id: componentId,
          mode: on ? "include" : "exclude",
        }).select("id");
        if (error) throw error; assertChanged(data);
      }
    },
    onSuccess: () => { invalidate(); toast({ title: "Selection saved" }); },
    onError: (e: any) => toast({ title: "Save failed", description: e.message, variant: "destructive" }),
  });

  const reset = useMutation({
    mutationFn: async () => {
      if (!companyId) throw new Error("No active company");
      const { data, error } = await supabase.from("company_brand_harvest_overrides").delete()
        .eq("company_id", companyId).ilike("brand", brand).eq("appliance_type", category).select("id");
      if (error) throw error; assertChanged(data);
    },
    onSuccess: () => { invalidate(); setConfirmReset(false); toast({ title: "Reset to platform defaults" }); },
    onError: (e: any) => { setConfirmReset(false); toast({ title: "Reset failed", description: e.message, variant: "destructive" }); },
  });

  const list = components.filter((c) =>
    (!c.appliance_category || c.appliance_category === category) &&
    (mode === "default" ? c.is_system_default : c.is_system_default || c.company_id === companyId),
  );

  return (
    <div className="space-y-4">
      <div className="bg-card rounded-xl border border-border p-4 grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Appliance category</label>
          <SearchableSelect
            value={category || null}
            onChange={(v) => { setCategory(v ?? ""); }}
            placeholder="Choose an appliance type…"
            searchPlaceholder="Search categories…"
            emptyMessage="No categories found"
            items={categories.map((c) => ({ value: c, label: c }))}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">Brand</label>
          <SearchableSelect
            value={brand || null}
            onChange={(v) => setBrand(v ?? "")}
            placeholder="Choose a brand…"
            searchPlaceholder="Search brands…"
            emptyMessage="No brands found"
            items={brands.map((b) => ({ value: b.name, label: b.name }))}
          />
        </div>
      </div>

      {ready && (
        <div className="bg-card rounded-xl border border-border p-4 space-y-2">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="text-sm text-muted-foreground">
              {mode === "company" ? (
                <>Select which components to harvest from <span className="font-medium text-foreground">{brand} {category}</span> appliances. Platform defaults are pre-selected; add or remove components for your company. Deselecting a component also hides it from harvest recommendations for these appliances.</>
              ) : (
                <>Select the default components to harvest from <span className="font-medium text-foreground">{brand} {category}</span> appliances. Every company starts with these; companies can add or remove components for themselves.</>
              )}
            </p>
            {mode === "company" && overrides.length > 0 && (
              <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={() => setConfirmReset(true)}>
                <RotateCcw className="w-3.5 h-3.5" /> Reset to platform defaults
              </Button>
            )}
          </div>
          {list.length === 0 && <p className="text-sm text-muted-foreground">No components for this category.</p>}
          {list.map((c) => {
            const isDefault = defaultIds.has(c.id);
            const ov = ovById.get(c.id);
            const companyDisabled = mode === "company" && c.is_system_default && disabledIds.has(c.id);
            const selected = mode === "default"
              ? isDefault
              : ov ? ov.mode === "include" : isDefault;
            return (
              <div key={c.id} className={`flex items-center justify-between gap-3 p-3 rounded-lg border border-border ${companyDisabled ? "opacity-60" : ""}`}>
                <label className="flex items-center gap-3 cursor-pointer min-w-0">
                  <Checkbox
                    checked={selected && !companyDisabled}
                    disabled={setSelected.isPending || companyDisabled}
                    onCheckedChange={(v) => setSelected.mutate({ componentId: c.id, on: !!v })}
                  />
                  <span className="font-medium text-foreground text-sm truncate">{c.name}</span>
                  {!c.is_system_default && <Badge variant="outline">Custom</Badge>}
                </label>
                <div className="flex gap-1.5 shrink-0">
                  {companyDisabled ? (
                    <Badge variant="secondary">Disabled for your company</Badge>
                  ) : mode === "company" ? (
                    ov?.mode === "include" ? <Badge>Added by your company</Badge>
                    : ov?.mode === "exclude" ? <Badge variant="destructive">Removed by your company</Badge>
                    : isDefault ? <Badge variant="secondary">Platform default</Badge> : null
                  ) : isDefault ? <Badge variant="secondary">Default</Badge> : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to platform defaults?</AlertDialogTitle>
            <AlertDialogDescription>
              Your company's additions and removals for {brand} {category} will be cleared. The platform defaults will apply again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => reset.mutate()} disabled={reset.isPending}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
